import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// The non-macro `useLingui` on purpose: the macro compiles down to a `_`
// destructure on the hook result, which explodes ("_t is not a function") in
// every existing test that stubs `@lingui/react` with just an `i18n`. This is a
// shared field renderer — it must survive those stubs.
import { useLingui } from "@lingui/react";
import { EnhancedSingleSelect } from "@/components/ui/enhanced-singleselect";
import type { EnhancedSelectOption, FieldConfig } from "../types";

interface AsyncSearchableSelectFieldProps<T> {
  field: FieldConfig<T>;
  value: string | undefined;
  onChange: (value: string) => void;
  onBlur: () => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export function AsyncSearchableSelectField<T>({
  field,
  value,
  onChange,
  disabled,
  id,
  placeholder,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: AsyncSearchableSelectFieldProps<T>) {
  const { i18n } = useLingui();
  const loadOptions = field.loadOptions;
  const initialOptions = useMemo<EnhancedSelectOption[]>(
    () => field.initialOptions ?? [],
    [field.initialOptions],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState<EnhancedSelectOption[]>(initialOptions);
  const [isSearching, setIsSearching] = useState(false);
  // A rejected loader is NOT an empty result set. Kept as explicit state so the
  // menu can say "the search failed" instead of the confident, wrong "no
  // results found" it used to render from the swallowed rejection.
  const [isError, setIsError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Remember the option behind the current `value` the moment we see it, from
  // either initialOptions or any search result. Selecting an option closes the
  // popover, which resets the search term, which re-runs loadOptions("") — and
  // that returns only the first page of server results. A selection the user
  // found via a narrow query is in neither that page nor initialOptions, so
  // without this it drops out of `options`, the trigger can no longer resolve a
  // label, and it renders the placeholder while the form value is still set —
  // the selection looks lost even though it isn't.
  const selectedOptionRef = useRef<EnhancedSelectOption | null>(null);
  useEffect(() => {
    if (!value) {
      selectedOptionRef.current = null;
      return;
    }
    if (selectedOptionRef.current?.value === value) return;
    const seen =
      options.find((opt) => opt.value === value) ??
      initialOptions.find((opt) => opt.value === value);
    if (seen) selectedOptionRef.current = seen;
  }, [value, options, initialOptions]);

  // Re-inject the remembered selection into a list that omits it.
  const withSelected = useCallback(
    (list: EnhancedSelectOption[]): EnhancedSelectOption[] => {
      const selected =
        selectedOptionRef.current?.value === value
          ? selectedOptionRef.current
          : initialOptions.find((opt) => opt.value === value);
      if (!selected) return list;
      if (list.some((opt) => opt.value === selected.value)) return list;
      return [selected, ...list];
    },
    [value, initialOptions],
  );

  // Keep the currently-selected option visible even when it isn't part of the
  // latest search results — otherwise the trigger's label would go blank when
  // the user types a query that doesn't match the persisted selection.
  useEffect(() => {
    setOptions((current) => {
      const merged = [...current];
      for (const opt of initialOptions) {
        if (!merged.some((existing) => existing.value === opt.value)) {
          merged.push(opt);
        }
      }
      return merged;
    });
  }, [initialOptions]);

  useEffect(() => {
    if (!loadOptions) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    let cancelled = false;
    setIsSearching(true);
    debounceRef.current = setTimeout(() => {
      loadOptions(searchTerm)
        .then((next) => {
          if (cancelled) return;
          setIsError(false);
          setOptions(withSelected(next));
        })
        .catch(() => {
          if (cancelled) return;
          setIsError(true);
          // Still keep whatever we can legitimately show (the seeded options and
          // the remembered selection) — the trigger must not go blank — but the
          // menu now reports the failure rather than an empty result set.
          setOptions(withSelected(initialOptions));
        })
        .finally(() => {
          if (!cancelled) setIsSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, loadOptions, initialOptions, withSelected]);

  return (
    <EnhancedSingleSelect
      id={id}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      value={value ?? ""}
      onChange={(next) => onChange(next)}
      // EnhancedSingleSelect's option value is `string`; the shared FieldConfig
      // option allows `string | number`, so coerce before passing (mirrors
      // MultiSelectField).
      options={options.map((opt) => ({
        value: String(opt.value),
        label: opt.label,
        emoji: opt.emoji,
        suggested: opt.suggested ?? false,
      }))}
      placeholder={placeholder}
      disabled={disabled}
      onSearchChange={setSearchTerm}
      isSearching={isSearching}
      isError={isError}
      errorMessage={i18n._({
        id: "form.asyncSelect.loadFailed",
        message: "Options could not be loaded. Adjust the search or try again.",
      })}
      // Optional rich option rendering (e.g. an avatar + name row).
      renderOption={field.renderOption}
    />
  );
}
